import { Controller } from "@hotwired/stimulus";

import debounce from "debounce";

const FOCUS_STORAGE_KEY_PREFIX = "search-form-preserve-focus"

// Connects to data-controller="search-form"
export default class extends Controller {
  static values = {
    preserveSearchFocus: { type: Boolean, default: false },
  };

  initialize() {
    // Debounce search submits so rapid typing does not flood Turbo requests.
    this.submit = debounce(this.submit.bind(this), 350);
  }

  connect() {
    this.restoreSearchFocus();

    if (this.preserveSearchFocusValue) {
      this.boundFrameLoad = this.onFrameLoad.bind(this);
      document.addEventListener("turbo:frame-load", this.boundFrameLoad);
    }
  }

  disconnect() {
    if (this.boundFrameLoad) {
      document.removeEventListener("turbo:frame-load", this.boundFrameLoad);
    }
  }

  onFrameLoad(event) {
    if (!this.preserveSearchFocusValue) return;

    const expected = this.element.dataset.turboFrame;
    if (!expected || event.target.id !== expected) return;

    this.restoreSearchFocus();
  }

  submit() {
    this.storeSearchFocus();
    this.element.requestSubmit();
  }

  static targets = [
    "form",
    "checkbox",
    "userTypesCheckbox",
    "userTypesHiddenField",
    "programTypesCheckbox",
    "programTypesHiddenField",
    "subscriptionTypesCheckbox",
    "subscriptionTypesHiddenField",
  ];

  updateHiddenField(event) {
    const checkbox = event.target;
    const hiddenFieldId = `${checkbox.id}_hidden`;
    const hiddenField = document.getElementById(hiddenFieldId);
    if (hiddenField) {
      hiddenField.value = checkbox.checked ? "true" : "false";
    }
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.submitForm();
    }, 200);
  }

  search() {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.storeSearchFocus();
      this.submitForm();
    }, 200);
  }

  submitForm() {
    const form = this.hasFormTarget ? this.formTarget : this.element;
    form.requestSubmit();
  }

  focusStorageKey() {
    const identity =
      this.element.id ||
      this.element.getAttribute("action") ||
      this.element.getAttribute("data-search-form-id") ||
      "default"
    return `${FOCUS_STORAGE_KEY_PREFIX}:${identity}`
  }

  storeSearchFocus() {
    if (!this.preserveSearchFocusValue) return;

    const input = this.searchInput();
    if (!input || document.activeElement !== input) return;

    sessionStorage.setItem(
      this.focusStorageKey(),
      JSON.stringify({
        selectionStart: input.selectionStart,
        selectionEnd: input.selectionEnd,
      })
    );
  }

  restoreSearchFocus() {
    if (!this.preserveSearchFocusValue) return;

    const raw = sessionStorage.getItem(this.focusStorageKey());
    if (!raw) return;

    sessionStorage.removeItem(this.focusStorageKey());

    let stored;
    try {
      stored = JSON.parse(raw);
    } catch {
      return;
    }

    const input = this.searchInput();
    if (!input) return;

    input.focus({ preventScroll: true });

    const start = Math.min(stored.selectionStart ?? input.value.length, input.value.length);
    const end = Math.min(stored.selectionEnd ?? start, input.value.length);
    input.setSelectionRange(start, end);
  }

  searchInput() {
    return this.element.querySelector('input[name="search"]');
  }
}
